// src/pages/Partnership.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Copy, Users, DollarSign, ArrowLeft, Check, RussianRuble,
  CreditCard, LogOut, Activity, Clock, Banknote, AlertCircle
} from 'lucide-react';
import { getPartnerCount, requestWithdrawal, getLastVivod } from '../api';
import Logo from '../assets/logo.svg';
import '../styles/Partnership.css';

function Partnership() {
  const [telegramId, setTelegramId] = useState(null);
  const [stats, setStats] = useState({
    partner_count: 0,
    active_referrals: 0,
    current_balance: 0,
    withdrawal_requests: 0,
    withdrawn_amount: 0,
    pending_amount: 0,
    other_status_amount: 0,
    has_partner_id: false,
  });
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const [lastVivod, setLastVivod] = useState({
    name: '',
    bank_name: '',
    card_number: ''
  });

  // Модалка вывода
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    card_number: '',
    name: '',
    bank_name: '',
  });
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawError, setWithdrawError] = useState(null);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);

  const MIN_WITHDRAW = 1000;

  useEffect(() => {
    let tgId = null;

    if (window.Telegram?.WebApp) {
      tgId = window.Telegram.WebApp.initDataUnsafe?.user?.id?.toString();
    }

    // dev fallback
    if (process.env.NODE_ENV === 'development' && !tgId) {
      const params = new URLSearchParams(window.location.search);
      tgId = params.get('tg_id') || '735189942';
    }

    if (tgId) {
      setTelegramId(tgId);
      loadStats(tgId);
    } else {
      setError("Не удалось определить Telegram ID");
      setLoading(false);
    }
  }, []);

  const loadStats = async (id) => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, lastVivodData] = await Promise.all([
        getPartnerCount(id),
        getLastVivod(id)
      ]);

      setStats({
        partner_count: statsData.partner_count ?? 0,
        active_referrals: statsData.active_referrals ?? 0,
        current_balance: statsData.current_balance ?? 0,
        withdrawal_requests: statsData.withdrawal_requests ?? 0,
        withdrawn_amount: statsData.withdrawn_amount ?? 0,
        pending_amount: statsData.pending_amount ?? 0,
        other_status_amount: statsData.other_status_amount ?? 0,
        has_partner_id: !!statsData.has_partner_id,
      });

      // Сохраняем последние реквизиты
      if (lastVivodData) {
        setLastVivod({
          name: lastVivodData.name || '',
          bank_name: lastVivodData.bank_name || '',
          card_number: lastVivodData.card_number || ''
        });
      }
    } catch (err) {
      console.error("Ошибка загрузки данных:", err);
      setError("Не удалось загрузить данные");
    } finally {
      setLoading(false);
    }
  };

  const openWithdrawModal = () => {
    // Заполняем форму последними данными
    setWithdrawForm({
      card_number: lastVivod.card_number,
      name: lastVivod.name,
      bank_name: lastVivod.bank_name,
    });
    setWithdrawError(null);
    setWithdrawSuccess(false);
    setShowWithdrawModal(true);
  };

  const botUsername = 'SuprunKEY_bot';
  const partnerLink = telegramId
    ? `https://t.me/${botUsername}?start=par_${telegramId}`
    : '';

  const copyToClipboard = () => {
    if (!partnerLink) return;
    navigator.clipboard.writeText(partnerLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  };

  const handleWithdrawInputChange = (e) => {
    const { name, value } = e.target;
    setWithdrawForm((prev) => ({ ...prev, [name]: value }));
    setWithdrawError(null);
  };

  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();
    setWithdrawError(null);
    setWithdrawSuccess(false);

    if (stats.current_balance < MIN_WITHDRAW) {
      setWithdrawError(`Минимальная сумма для вывода — ${MIN_WITHDRAW} ₽`);
      return;
    }

    if (!withdrawForm.card_number.trim() || !withdrawForm.name.trim() || !withdrawForm.bank_name.trim()) {
      setWithdrawError('Заполните все поля');
      return;
    }

    const cardClean = withdrawForm.card_number.replace(/\D/g, '');
    if (cardClean.length < 16 || cardClean.length > 19) {
      setWithdrawError('Номер карты должен содержать 16–19 цифр');
      return;
    }

    setWithdrawLoading(true);

    try {
      await requestWithdrawal(
        telegramId,
        withdrawForm.card_number,
        withdrawForm.name,
        withdrawForm.bank_name
      );

      setWithdrawSuccess(true);

      // Обновляем локальное состояние после успешного запроса
      setStats((prev) => ({
        ...prev,
        current_balance: 0,
        pending_amount: prev.pending_amount + prev.current_balance,
        withdrawal_requests: prev.withdrawal_requests + 1,
      }));

      setWithdrawForm({ card_number: '', name: '', bank_name: '' });

      setTimeout(() => {
        setShowWithdrawModal(false);
        setWithdrawSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Ошибка вывода:', err);
      setWithdrawError(err.message || 'Не удалось создать заявку на вывод');
    } finally {
      setWithdrawLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="partnership-wrapper">
        <div className="loading">Загрузка партнёрской статистики...</div>
      </div>
    );
  }

  if (error || !telegramId) {
    return (
      <div className="partnership-wrapper">
        <div className="error-message">
          <p>{error || "Не удалось определить ваш Telegram ID"}</p>
        </div>
      </div>
    );
  }

  const totalEverEarned = (stats.current_balance + stats.withdrawn_amount + stats.pending_amount).toFixed(2);

  return (
    <div className="partnership-wrapper">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="partnership-content"
      >
        <div className="header">
          <img src={Logo} alt="SuprunVPN" className="logo-image" />
        </div>

        <div className="header-with-back">
          <motion.button
            className="back-button"
            onClick={() => window.history.back()}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft size={24} />
          </motion.button>
          
        </div>

        <h1 className="title">Партнёрская программа</h1>

        <p className="subtitle">
          Приглашайте друзей и получайте процент от их подписок
        </p>

        {/* Основные показатели */}
        <div className="stats-grid">
          <div className="stat-card">
            <Users size={32} className="stat-icon users" />
            <div className="stat-value">{stats.partner_count}</div>
            <div className="stat-label">Всего друзей</div>
          </div>

          <div className="stat-card">
            <Activity size={32} className="stat-icon active" />
            <div className="stat-value">{stats.active_referrals}</div>
            <div className="stat-label">Активных</div>
          </div>

          <div className="stat-card">
            <RussianRuble size={32} className="stat-icon balance" />
            <div className="stat-value">{stats.current_balance} ₽</div>
            <div className="stat-label">Доступно</div>
          </div>

          <div className="stat-card">
            <Banknote size={32} className="stat-icon withdrawn" />
            <div className="stat-value">{totalEverEarned} ₽</div>
            <div className="stat-label">Всего заработано</div>
          </div>
        </div>

        {/* Детализация выводов */}
        {stats.pending_amount !== 0 && (
        <div className="withdraw-stats-grid">
            <div className="withdraw-stat-item">
            <Clock size={20} />
            <span>
                В обработке на вывод: <strong>{stats.pending_amount} ₽</strong>
            </span>
            </div>
        </div>
        )}

        {/* Кнопка вывода */}
        <motion.button
          className={`withdraw-button ${stats.current_balance < MIN_WITHDRAW ? 'disabled' : ''}`}
          onClick={openWithdrawModal}   
          disabled={stats.current_balance < MIN_WITHDRAW || loading}
          whileTap={{ scale: 0.96 }}
        >
          <LogOut size={20} />
          Вывести средства
        </motion.button>

        {stats.current_balance > 0 && stats.current_balance < MIN_WITHDRAW && (
          <div className="withdraw-hint">
            Доступно от {MIN_WITHDRAW} ₽
          </div>
        )}

        {/* Реферальная ссылка */}
        <div className="referral-block">
          <div className="referral-title">Ваша реферальная ссылка</div>
          
          <div className="link-box">
            <div className="link-text">{partnerLink || '—'}</div>
            <motion.button
              className={`copy-button ${copied ? 'success' : ''}`}
              onClick={copyToClipboard}
              disabled={!partnerLink}
              whileTap={{ scale: 0.96 }}
            >
              {copied ? <Check size={20} /> : <Copy size={20} />}
            </motion.button>
          </div>

          {copied && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="copied-notification"
            >
              Ссылка скопирована!
            </motion.div>
          )}
        </div>

        <div className="info-section">
          <h3>Как работает партнёрка</h3>
          <ul>
            <li>Поделитесь своей уникальной ссылкой</li>
            <li>Друг переходит и покупает подписку</li>
            <li>Вы получаете % от его платежа</li>
            <li>Вывод от {MIN_WITHDRAW} ₽</li>
          </ul>
        </div>
      </motion.div>

      {/* Модальное окно вывода */}
      {showWithdrawModal && (
        <div className="modal-overlay" onClick={() => setShowWithdrawModal(false)}>
          <motion.div
            className="withdraw-modal"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Вывод средств</h2>
            <p className="modal-balance">
              Доступно: <strong>{stats.current_balance} ₽</strong>
            </p>

            {withdrawSuccess ? (
              <div className="success-message">
                <Check size={48} />
                <p>Заявка создана!<br/>Средства будут переведены в ближайшее время</p>
              </div>
            ) : (
              <form onSubmit={handleWithdrawSubmit}>
                <div className="form-group">
                  <label>Номер карты</label>
                  <input
                    type="text"
                    name="card_number"
                    value={withdrawForm.card_number}
                    onChange={handleWithdrawInputChange}
                    placeholder="2202 2002 2002 2002"
                    maxLength={19}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Имя получателя</label>
                  <input
                    type="text"
                    name="name"
                    value={withdrawForm.name}
                    onChange={handleWithdrawInputChange}
                    placeholder="Иванов Иван Иванович"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Банк</label>
                  <input
                    type="text"
                    name="bank_name"
                    value={withdrawForm.bank_name}
                    onChange={handleWithdrawInputChange}
                    placeholder="Тинькофф / Сбер / Альфа-Банк"
                    required
                  />
                </div>

                {withdrawError && (
                  <div className="form-error">{withdrawError}</div>
                )}

                <div className="modal-buttons">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setShowWithdrawModal(false)}
                    disabled={withdrawLoading}
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={withdrawLoading}
                  >
                    {withdrawLoading ? 'Отправка...' : 'Вывести'}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default Partnership;